import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PesoBadge from './PesoBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Sparkles, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

const AISuggestionsModal = ({ open, onOpenChange, profile }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({
    profile_improvements: [],
    job_matches: [],
    gig_matches: []
  });

  useEffect(() => {
    if (open) {
      fetchSuggestions();
    }
  }, [open]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('modal.pleaseLogin'));
        return;
      }

      const response = await api.get('/api/ai/suggestions', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      toast.error(t('modal.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleJobClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
    onOpenChange(false);
  };

  // Helper to get gig URL (use slug if available, otherwise ID)
  const getGigUrl = (gig) => {
    return gig.slug ? `/gigs/${gig.slug}` : `/gigs/${gig.id || gig.gig_id}`;
  };

  const handleGigClick = (gig) => {
    navigate(getGigUrl(gig));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="text-primary" size={28} />
            {t('modal.careerRecommendations')}
          </DialogTitle>
          <DialogDescription>
            {t('modal.personalizedRecommendations')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 size={48} className="animate-spin text-primary" />
          </div>
        ) : (
          <ScrollArea className="max-h-[calc(85vh-10rem)]">
            <div className="space-y-6 pr-4">
              {/* Job Matches */}
              {suggestions.job_matches.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="text-green-500" size={20} />
                    <h3 className="font-heading text-lg font-semibold">
                      {t('modal.jobsMatchProfile')} ({suggestions.job_matches.length})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {suggestions.job_matches.map((job) => (
                      <Card
                        key={job.id}
                        className={`hover:shadow-md transition-shadow cursor-pointer ${job.is_peso_match ? 'border-2 border-[#1d4ed8] bg-blue-50/40' : ''}`}
                        onClick={() => handleJobClick(job.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h4 className="font-semibold">{job.title}</h4>
                                {job.is_peso_match && (
                                  <PesoBadge officeName={job.peso_office_name} color={job.peso_office_color} title={t('peso.recPriorityNote')} />
                                )}
                                {job.is_new && (
                                  <Badge className="bg-green-500 text-white text-xs">New</Badge>
                                )}
                                {job.is_local && (
                                  <Badge variant="outline" className="text-xs bg-blue-50">📍 {t('modal.nearYou')}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">
                                {job.employer_name && <span className="font-medium">{job.employer_name} • </span>}
                                {job.location}
                              </p>
                              {(job.salary_min || job.salary_max) && (
                                <p className="text-sm font-medium text-primary mb-2">
                                  ₱{job.salary_min?.toLocaleString()} - ₱{job.salary_max?.toLocaleString()}
                                  {job.salary_period && <span className="text-xs text-muted-foreground"> per {job.salary_period === 'daily' ? 'day' : 'month'}</span>}
                                </p>
                              )}
                              {job.match_reason ? (
                                <p className="text-sm text-muted-foreground italic">
                                  {i18n.language === 'ceb' && job.match_reason_ceb ? job.match_reason_ceb : job.match_reason}
                                </p>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {(job.matched_keywords || []).map((keyword, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {keyword}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className="bg-green-500" title="AI match score based on skills, experience, location, and salary fit">
                                {job.match_score}% {t('modal.match')}
                              </Badge>
                              <ArrowRight size={16} className="text-muted-foreground" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Gig Matches */}
              {suggestions.gig_matches.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="text-orange-500" size={20} />
                    <h3 className="font-heading text-lg font-semibold">
                      {t('modal.gigsMatchProfile')} ({suggestions.gig_matches.length})
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {suggestions.gig_matches.map((gig) => (
                      <Card
                        key={gig.id}
                        className={`hover:shadow-md transition-shadow cursor-pointer ${gig.is_peso_match ? 'border-2 border-[#1d4ed8] bg-blue-50/40' : ''}`}
                        onClick={() => handleGigClick(gig)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h4 className="font-semibold">{gig.title}</h4>
                                {gig.is_peso_match && (
                                  <PesoBadge officeName={gig.peso_office_name} color={gig.peso_office_color} title={t('peso.recPriorityNote')} />
                                )}
                                {gig.is_new && (
                                  <Badge className="bg-green-500 text-white text-xs">New</Badge>
                                )}
                                {gig.is_local && (
                                  <Badge variant="outline" className="text-xs bg-blue-50">📍 {t('modal.nearYou')}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">
                                {gig.employer_name && <span className="font-medium">{gig.employer_name} • </span>}
                                {gig.location}
                              </p>
                              {(gig.salary_min || gig.salary_max) && (
                                <p className="text-sm font-medium text-primary mb-2">
                                  ₱{gig.salary_min?.toLocaleString()} - ₱{gig.salary_max?.toLocaleString()}
                                </p>
                              )}
                              {gig.match_reason ? (
                                <p className="text-sm text-muted-foreground italic">
                                  {i18n.language === 'ceb' && gig.match_reason_ceb ? gig.match_reason_ceb : gig.match_reason}
                                </p>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {(gig.matched_keywords || []).map((keyword, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {keyword}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className="bg-orange-500" title="AI match score based on skills, experience, location, and salary fit">
                                {gig.match_score}% {t('modal.match')}
                              </Badge>
                              <ArrowRight size={16} className="text-muted-foreground" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {suggestions.job_matches.length === 0 && suggestions.gig_matches.length === 0 && (
                <div className="text-center py-12">
                  <Sparkles className="mx-auto mb-4 text-muted-foreground" size={48} />
                  <h3 className="font-heading text-xl font-semibold mb-2">{t('modal.allSet')}</h3>
                  <p className="text-muted-foreground">
                    {t('modal.profileLookingGreat')}
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <div className="border-t pt-4">
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="w-full rounded-xl"
          >
            {t('modal.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AISuggestionsModal;
